import React from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, FileText, X } from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useQuote } from "@/context/QuoteContext";
import { useState } from "react";
import LegalLink from "@/components/LegalDialog";

export default function QuoteDrawer() {
  const { items, freeRequests, open, setOpen, updateItem, removeItem, addFree, removeFree, kalemCount, ambalajCount } = useQuote();
  const [free, setFree] = useState("");
  const navigate = useNavigate();

  const totalKg = (it) =>
    it.ambalaj_miktari ? `${(it.ambalaj_miktari * it.adet).toLocaleString("tr-TR")} ${it.ambalaj_birimi || ""}` : null;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col p-0" data-testid="quote-drawer">
        <SheetHeader className="p-5 border-b bg-slate-50">
          <SheetTitle className="flex items-center gap-2 font-head">
            <FileText className="w-5 h-5 text-teal-600" />
            Teklif Listem
            <Badge variant="secondary" className="ml-auto" data-testid="quote-drawer-count">
              {kalemCount} kalem · {ambalajCount} ambalaj
            </Badge>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {items.length === 0 && freeRequests.length === 0 && (
            <div className="text-center text-slate-500 py-16 text-sm" data-testid="quote-empty">
              Teklif listeniz boş.<br />Kataloğdan ürün ekleyerek başlayın.
            </div>
          )}

          {items.map((it) => (
            <div key={it.urun_id} className="border rounded-lg p-3 bg-white" data-testid={`quote-item-${it.urun_id}`}>
              <div className="flex justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm text-slate-900 truncate">{it.urun_adi}</div>
                  <div className="text-xs text-slate-500 font-mono">{it.urun_kodu} · {it.marka}</div>
                </div>
                <button onClick={() => removeItem(it.urun_id)} data-testid={`remove-item-${it.urun_id}`}
                  className="text-slate-400 hover:text-red-500 shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-xs text-slate-600">Ambalaj: <b>{it.ambalaj || "—"}</b></span>
                <div className="flex items-center gap-1 ml-auto border rounded-md">
                  <button className="p-1.5 hover:bg-slate-100" data-testid={`dec-${it.urun_id}`}
                    onClick={() => updateItem(it.urun_id, { adet: Math.max(1, it.adet - 1) })}>
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <Input type="number" min={1} value={it.adet} data-testid={`qty-${it.urun_id}`}
                    onChange={(e) => updateItem(it.urun_id, { adet: Math.max(1, parseInt(e.target.value) || 1) })}
                    className="w-14 h-8 text-center border-0 p-0" />
                  <button className="p-1.5 hover:bg-slate-100" data-testid={`inc-${it.urun_id}`}
                    onClick={() => updateItem(it.urun_id, { adet: it.adet + 1 })}>
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="text-xs text-teal-700 mt-1.5 font-medium">
                {it.adet} ambalaj {totalKg(it) ? `· toplam ${totalKg(it)}` : ""}
              </div>
              <Textarea placeholder="Ürüne özel not (opsiyonel)" value={it.musteri_notu}
                data-testid={`note-${it.urun_id}`}
                onChange={(e) => updateItem(it.urun_id, { musteri_notu: e.target.value })}
                className="mt-2 text-xs min-h-[36px] h-9" />
            </div>
          ))}

          {freeRequests.map((f, i) => (
            <div key={i} className="border border-dashed rounded-lg p-3 bg-amber-50 flex justify-between gap-2 text-sm">
              <div><span className="text-amber-700 font-medium">Serbest talep:</span> {f}</div>
              <button onClick={() => removeFree(i)} className="text-slate-400 hover:text-red-500">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}

          <div className="border rounded-lg p-3 bg-slate-50">
            <label className="text-xs font-medium text-slate-700">Listede olmayan ürün / serbest talep</label>
            <Textarea value={free} onChange={(e) => setFree(e.target.value)} data-testid="free-request-input"
              placeholder="Katalogda bulamadığınız bir ürünü buraya yazabilirsiniz." className="mt-1.5 text-xs" />
            <Button size="sm" variant="outline" className="mt-2" data-testid="add-free-request-btn"
              onClick={() => { if (free.trim()) { addFree(free.trim()); setFree(""); } }}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Talebe Ekle
            </Button>
          </div>
        </div>

        <SheetFooter className="p-5 border-t bg-white flex-col sm:flex-col gap-3">
          <Button className="w-full bg-teal-600 hover:bg-teal-700 h-11" data-testid="goto-quote-request-btn"
            disabled={items.length === 0 && freeRequests.length === 0}
            onClick={() => { setOpen(false); navigate("/teklif-iste"); }}>
            Teklif Talebine Geç
          </Button>
          <p className="text-xs text-slate-500 text-center" data-testid="drawer-legal-links">
            Kişisel verileriniz hakkında: <LegalLink slug="aydinlatma">Aydınlatma Metni</LegalLink>
            {" · "}<LegalLink slug="gizlilik">Gizlilik Politikası</LegalLink>
          </p>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
